import { useEffect, useState } from "react";
import { connect, RootStateOrAny } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { Link, useHistory } from "react-router-dom";
import moment from "moment";

import { Breadcrumb, Typography } from "antd";
import { EditOutlined, HomeOutlined } from "@ant-design/icons";

import { UserRole, ClaimList } from "utils/enums";
import { getDisplayCompanyId } from "utils/helpers";
import notifier from "utils/notifier";
import { ErrorResponse } from "utils/types";

import { getClaimRead } from "store/claim/providerList/actions";
import { search, uploadAttachment } from "store/claim/actions";
import { getHelpCenterLinks } from 'store/app/helpCenter/actions';

import SearchClaimsForm from "components/Claims/SearchClaims/SearchClaimsForm";
import SearchClaimDetail from "components/Claims/SearchClaims/SearchDetail";
import AttachmentModal from "components/Claims/AttachmentModal";
import TableResult from "components/Common/TableResult";
import HelpCenterLink from 'components/Common/HelpCenterLink';

import styles from "./SearchClaims.module.scss";

const { Title } = Typography;

interface SearchClaimsPageProps {
  getClaimRead(userId: string): any;
  isFetchingClaimRead: boolean;
  search(searchObject: Object): any;
  isSearching: boolean;
  uploadAttachment(uploadObject: Object): any;
  isUploading: boolean;
  getHelpCenterLinks(companyId: string, page: string, type: string): any;
}

const SearchClaimsPage = (props: SearchClaimsPageProps) => {
  const history = useHistory();

  const [page, setPage] = useState("start");
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [visible, setVisible] = useState(false);
  const [claimNo, setClaimNo] = useState("");
  const [claimStatus, setClaimStatus] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [helpCenter, setHelpCenter] = useState<any>([]);

  useEffect(() => {
    getHelpCenterLinks();
  }, [])

  const getHelpCenterLinks = () => {
    props.getHelpCenterLinks(sessionStorage.getItem('ipaGroup')!, '/claims/search', 'page')
      .then((response: any) => {
        setHelpCenter(response);
    })
    .catch((err: ErrorResponse) => {
      console.log(err?.response?.data?.Message);
    });
  }

  const columns = [
    {
      title: "IPA",
      key: "RmiCompanyId",
      dataIndex: "RmiCompanyId",
      render: (text: string) => getDisplayCompanyId(text),
    },
    {
      title: "Claim Number",
      key: "RmiClaimNo",
      dataIndex: "RmiClaimNo",
      sorter: (a: any, b: any) => a.RmiClaimNo.localeCompare(b.RmiClaimNo),
      render: (text: string, record: any) =>
        sessionStorage.getItem("user_role") === UserRole.THIRD_PARTY ? (
          text
        ) : (
          <a onClick={() => handleClaimDetailOpen(record)}>{text}</a>
        ),
    },
    {
      title: "Member ID",
      key: "RmiMembId",
      dataIndex: "RmiMembId",
      sorter: (a: any, b: any) => a.RmiMembId.localeCompare(b.RmiMembId),
    },
    {
      title: "Member Name",
      key: "RmiMembName",
      dataIndex: "RmiMembName",
      sorter: (a: any, b: any) => a.RmiMembName.localeCompare(b.RmiMembName),
    },
    {
      title: "Billing Provider",
      key: "RmiBillingProvider",
      dataIndex: "RmiBillingProvider",
    },
    {
      title: "Status",
      key: "RmiStatus",
      dataIndex: "RmiStatus",
      sorter: (a: any, b: any) => a.RmiStatus.localeCompare(b.RmiStatus),
    },
    {
      title: "Date Received",
      key: "RmiRecdDate",
      dataIndex: "RmiRecdDate",
      sorter: (a: any, b: any) => a.RmiRecdDate.localeCompare(b.RmiRecdDate),
      render: (text: string) =>
        text ? moment(text).format("MM/DD/YYYY") : null,
    },
    {
      title: "DOS",
      key: "RmiDateFrom",
      dataIndex: "RmiDateFrom",
      sorter: (a: any, b: any) => a.RmiDateFrom.localeCompare(b.RmiDateFrom),
      render: (text: string) =>
        text ? moment(text).format("MM/DD/YYYY") : null,
    },
    {
      title: "Date Paid",
      key: "RmiDatePaid",
      dataIndex: "RmiDatePaid",
      sorter: (a: any, b: any) => a.RmiDatePaid?.localeCompare(b.RmiDatePaid),
      render: (text: string) =>
        text ? moment(text).format("MM/DD/YYYY") : null,
    },
    {
      title: "Attachment",
      key: "attach",
      dataIndex: "attach",
      render: (text: string, record: any) =>
        sessionStorage.getItem("user_role") === UserRole.THIRD_PARTY ? (
          "Add Attachment"
        ) : (
          <a onClick={() => handleAttachmentOpen(record.RmiClaimNo)}>
            {record.RmiDatePaid === null ? "Add Attachment" : null}
          </a>
        ),
    },
  ];

  const handleAttachmentOpen = (claimNo: string) => {
    setClaimNo(claimNo);
    setVisible(true);
  };

  const handleClaimDetailOpen = (record: any) => {
    setClaimNo(record.RmiClaimNo);
    setClaimStatus(record.RmiStatus);
    setCompanyId(record.RmiCompanyId);
    setPage("detail");
  };

  return (
    <div className={styles["search-claims-page"]}>
      <div className={styles["header-title"]}>
        <Title level={4}>&nbsp;&nbsp;&nbsp;Search Claims</Title>
      </div>

      {page === "start" && (
        <>
          {sessionStorage.getItem("user_role") !== UserRole.THIRD_PARTY && (
            <div>
              <Breadcrumb>
                <Breadcrumb.Item>
                  <HomeOutlined
                    onClick={() => history.push("/quality/overview")}
                  />
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                  <EditOutlined />
                  <a href="#">Search Claims</a>
                </Breadcrumb.Item>
              </Breadcrumb>
              {helpCenter?.length > 0
                ? helpCenter.map((hc: any) => {
                    return (
                      <>
                        <div className={`row ${styles["help-center-link"]}`}>
                          <div className="col-md-24">
                            <HelpCenterLink
                              clickEventId={hc.ClickEventId}
                              linkName={hc.LinkName}
                              pagePath={hc.PagePath}
                              url={hc.Link}
                              companyId={hc.CompanyId}
                            />
                          </div>
                        </div>
                      </>
                    );
                  })
                : null
              }
            </div>
          )}
          <div className={styles["search-section"]}>
            <SearchClaimsForm
              getClaimRead={props.getClaimRead}
              isFetchingClaimRead={props.isFetchingClaimRead}
              search={props.search}
              isSearching={props.isSearching}
              setDataSource={setDataSource}
            />
          </div>

          {dataSource.length !== 0 && (
            <div className={styles["content"]}>
              {dataSource.length === ClaimList.SearchClaimMaxLimit && (
                <p>
                  Search results are limited to recent 500 items. If you dont
                  find what you were looking for, try again using more specific
                  search terms.
                </p>
              )}
              <TableResult
                dataSource={dataSource}
                columns={columns}
                loading={false}
                showSizeChanger={true}
              />
            </div>
          )}
        </>
      )}

      {page === "detail" && (
        <>
          <div>
            <Breadcrumb>
              <Breadcrumb.Item>
                <HomeOutlined
                  onClick={() => history.push("/quality/overview")}
                />
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <EditOutlined />
                <Link to="/claims/search" onClick={() => setPage("start")}>
                  Search Claims
                </Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <EditOutlined />
                <a href="#">Claim Detail</a>
              </Breadcrumb.Item>
            </Breadcrumb>
          </div>

          <SearchClaimDetail
            setPage={setPage}
            claimNo={claimNo}
            status={claimStatus}
            companyId={companyId}
          />
        </>
      )}

      <AttachmentModal
        uploadAttachment={props.uploadAttachment}
        isUploading={props.isUploading}
        isAttachmentModalVisible={visible}
        setVisible={setVisible}
        claimNo={claimNo}
      />
    </div>
  );
};

const mapStateToProps = (store: RootStateOrAny) => ({
  isFetchingClaimRead: store.rootReducer.claimProviderList.isFetching,
  isSearching: store.rootReducer.claim.isSearching,
  isUploading: store.rootReducer.claim.isUploading,
});

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      getClaimRead,
      search,
      uploadAttachment,
      getHelpCenterLinks,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(SearchClaimsPage);
